import { AxiosError } from 'axios';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { MunicipalityNomenclator } from '@/interfaces/ServerInterfaces';

import APIServices from '../../api/APIServices';

export interface MunicipalitiesQueryParams {
  all_data?: boolean
  provinceId?: number
  search?: string
}

// Create a unique, descriptive key for this query
const MUNICIPALITIES_QUERY_KEY = 'municipalities'

type MunicipalitiesResponse = {
  items: MunicipalityNomenclator[]
  totalItems: number
  currentPage: number
  totalPages: number
}

/**
 * Fetch municipalities from the API
 * Separating API call logic from the hook for better testability
 */
const fetchMunicipalities = async (
  params?: MunicipalitiesQueryParams
): Promise<MunicipalitiesResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value != null)
  )

  const response = await APIServices.get('/invoice/public/municipalities', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching municipalities
 *
 * @param params - Query parameters for filtering municipalities
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useMunicipalitiesQuery = ({
  params,
  options,
}: {
  params: MunicipalitiesQueryParams
  options?: Partial<UseQueryOptions<MunicipalitiesResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<MunicipalitiesResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [MUNICIPALITIES_QUERY_KEY, params],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchMunicipalities(params),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes (location data doesn't change often)

    // Apply any custom options
    ...options,
  })

  return {
    municipalities: queryResult.data?.items || [],
    isLoadingMunicipalities: queryResult.isLoading,
    isErrorMunicipalities: queryResult.isError,
    errorMunicipalities: queryResult.error,
    refetchMunicipalities: queryResult.refetch,
    isFetchingMunicipalities: queryResult.isFetching,
  }
}

/**
 * Helper function to get the municipalities query key
 * Useful for cache invalidation or prefetching
 */
export const getMunicipalitiesQueryKey = (
  params?: MunicipalitiesQueryParams
) => [MUNICIPALITIES_QUERY_KEY, params]
